import { TaskProgressChecklist } from '../../../components/ui/task-progress-checklist';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskView } from '@apm/contracts';
import { MarkdownContent } from '../../../components/ui/markdown-content';
import { cn } from '../../../lib/utils';
import { ActivityFeed } from './activity-feed';
import { AttachmentList } from './attachment-list';
import { TaskBugLinks } from '../../testing/components/task-bug-links';
import { TaskDecisionSheets } from '../../decision-sheets/components/task-decision-sheets';
import { CommentThread } from './comment-thread';
import { TaskActualTime } from './task-actual-time';
import { TaskDodChecklist } from './task-dod-checklist';
import { TaskEstimateSection } from './task-estimate-section';
import { CollapsibleSection } from '../../../components/ui/collapsible-section';
import { DeleteTaskButton } from '../../tasks/components/delete-task-button';
import { isOffPlan } from '../../tasks/off-plan';
import { GlassIconButton } from '../../../components/ui/glass-icon-button';
import { TaskSyncLine } from '../../project-sync/components/task-sync-line';
import { GitDevelopmentBlock } from '../../git/components/git-development-block';
import { TaskProgressCommits } from '../../git/components/task-progress-commits';
import { branchSubjectOfTask } from '../../git/git-format';

type Tab = 'comments' | 'activity' | 'attachments';

/**
 * Chi tiết một task: bình luận, lịch sử thay đổi, tệp đính kèm (spec mục 4).
 *
 * Drawer bên phải chứ không phải trang riêng: người dùng đang ở board và cần
 * quay lại board ngay — chuyển trang làm mất ngữ cảnh cột nào đang xem tới đâu.
 */
export function TaskDetailDrawer({
  task,
  currentUserId,
  canEdit: canEditProject,
  onClose,
  onEdit,
  progress,
  commentsDeletable = true,
  attachmentsUploadable = false,
}: {
  task: TaskView;
  currentUserId: string;
  /**
   * Quyền `task:update` **cấp dự án** của người xem. Drawer tự gộp thêm
   * `task.canEdit` — quyền trên đúng việc này (APM-TASK-own): nhân viên chỉ sửa
   * được việc giao cho mình hoặc do mình tạo.
   */
  canEdit: boolean;
  onClose: () => void;
  /**
   * Mở form sửa — APM-1308b. Bỏ trống thì không hiện nút.
   *
   * Drawer **không** tự dựng form: nó sống trong board, và form cần đóng drawer
   * lại để không có hai lớp phủ chồng nhau. Ai mở drawer thì người đó quyết
   * định chuyện gì xảy ra khi bấm Sửa.
   */
  onEdit?: () => void;
  /**
   * Ô cập nhật tiến độ, chèn ngay dưới header — APM-1319.
   *
   * Drawer không tự biết cách sửa tiến độ (đó là `ProgressQuickAction`, việc
   * của người gọi): board đã có cách cập nhật ngay trên thẻ nên không cần
   * truyền; "Việc của tôi" thì dòng danh sách chỉ hiện % chứ không sửa được,
   * nên popup này là chỗ duy nhất để làm việc đó.
   */
  progress?: ReactNode;
  /** Xem `CommentThread.canDelete`. Mặc định giữ nguyên hành vi cũ. */
  commentsDeletable?: boolean;
  /** Xem `AttachmentList.canUpload`. Mặc định giữ nguyên hành vi cũ (tắt). */
  attachmentsUploadable?: boolean;
}): ReactNode {
  /*
    Nút Sửa (và mọi ô sửa trong drawer) ẩn hẳn khi không sửa được việc này —
    người dùng chốt 2026-09-25. Trước đây Board và Gantt chỉ truyền quyền cấp
    dự án, nên nhân viên thấy nút Sửa trên việc của người khác, bấm vào rồi mới
    bị từ chối.
  */
  const canEdit = canEditProject && (task.canEdit ?? true);
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('comments');

  // Esc để đóng — thao tác mà ai cũng thử đầu tiên khi muốn thoát một lớp phủ.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40 popup-scrim" onClick={onClose} aria-hidden />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={task.title}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-border bg-background shadow-xl glass-popup-md on-scrim"
      >
        <header className="flex items-start gap-3 border-b border-border p-4">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-xs text-muted-foreground">{task.key}</div>
            <h2 className="truncate text-base font-semibold">{task.title}</h2>
            <div className="mt-1 text-xs text-muted-foreground">
              {task.statusName}
              {task.assigneeName && ` · ${task.assigneeName}`}
            </div>

            {/* Việc kéo về từ nguồn ngoài — APM-SYNC-h (§7.4). Không đồng bộ thì không hiện gì. */}
            <TaskSyncLine projectId={task.projectId} taskId={task.id} />

            {/*
              Người tạo — APM-TASK-creator.

              Chỉ hiện khi tra được tên: `createdById` có thể `null` (Replay,
              bản nháp) hoặc người tạo đã rời tổ chức. Dấu "ngoài kế hoạch" thì
              chỉ khi `createdByPm === false` — `null` là *chưa biết*, và một
              dấu dán lên chỗ không có dữ liệu còn tệ hơn không có dấu.
            */}
            {task.createdByName === null ? null : (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t('board.createdBy')}: {task.createdByName}
                {isOffPlan(task) ? (
                  <span
                    className="ml-1.5 rounded-full bg-rag-amber/15 px-1.5 py-0.5 text-rag-amber"
                    title={t('board.createdByOtherTitle', { name: task.createdByName })}
                  >
                    {t('board.createdByOther')}
                  </span>
                ) : null}
              </div>
            )}
          </div>

          {canEdit && onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="btn-glass rounded-full px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {t('taskForm.edit')}
            </button>
          )}

          <DeleteTaskButton task={task} onDeleted={onClose} />

          <GlassIconButton variant="close" onClick={onClose} label={t('common.close')} />
        </header>

        {/*
          Mọi thứ dưới header cuộn **chung** một vùng — người dùng báo 2026-10-06.

          Trước đây chỉ vùng tab ở đáy (`flex-1 overflow-y-auto`) cuộn được, còn
          tóm tắt, mô tả, checklist, tiến độ, thời gian, DoD và thanh tab đều không
          co. Task có mô tả dài cộng checklist là phần trên đã cao hơn màn hình:
          vùng tab bị ép còn 0px, ô bình luận nằm ngoài khung và không có thanh
          cuộn nào để tới được. Header (mã, tên, nút đóng) vẫn đứng yên ở trên.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {/*
            Khối "Tóm tắt việc này" (AI) đã bỏ khỏi drawer — người dùng yêu cầu
            2026-11-02. Skill `task_summary` vẫn còn, chỉ không còn chỗ gọi ở đây.
          */}
          {task.description && (
            <section
              aria-label={t('taskForm.description')}
              className="max-h-64 shrink-0 overflow-y-auto border-b border-border px-4 py-3"
            >
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">
                {t('taskForm.description')}
              </h3>
              <MarkdownContent value={task.description} />
            </section>
          )}

          {/*
          Sổ chốt phương án mà việc này nhúng — APM-DSX-d. Đứng ngay dưới mô tả:
          với việc "trả lời sổ", sổ **là** nội dung của việc. Tự ẩn khi rỗng.
        */}
          <TaskDecisionSheets projectId={task.projectId} taskId={task.id} />

          {task.checklist && (
            <div className="max-h-72 shrink-0 overflow-y-auto border-b border-border px-4 py-3">
              <TaskProgressChecklist key={task.id} task={task} />
            </div>
          )}

          {progress && <div className="border-b border-border px-4 py-3">{progress}</div>}

          {/*
          Kế hoạch cạnh thực tế — APM-TASK-j.

          Đứng **trên** ba tab và dưới ô tiến độ: nó trả lời "việc này chạy đúng
          như tính không", câu mà người mở một việc ra xem hỏi ngay — còn bình
          luận, hoạt động và tệp là những thứ người ta chủ động đi tìm.
        */}
          {/* SP ước lượng + việc mẫu đã so — người dùng yêu cầu 2026-11-02. Không gập. */}
          <TaskEstimateSection task={task} canEdit={canEdit} />

          {/*
            Gập sẵn — người dùng yêu cầu 2026-11-02: bảng ba cột là thứ tra khi cần,
            không phải thứ đọc mỗi lần mở việc. Cùng luật với khối "Phát triển".
          */}
          <CollapsibleSection
            title={t('actualTime.title')}
            className="border-b border-border px-4 py-3"
          >
            {/* Quyền riêng (APM-ACTH-b): Leader sửa được giờ mà không sửa được việc. */}
            <TaskActualTime task={task} />
          </CollapsibleSection>

          {/*
          Khối "Phát triển" — APM-GIT-e (docs/plan-git-integration.md §8.2): nhánh,
          MR, commit nối với việc này, và nút sao chép tên nhánh.

          Đứng trên ba tab vì "việc này đã có code chưa, MR tới đâu" là câu người
          mở việc ra hỏi cùng lúc với tiến độ. Khối tự hỏi `git:read`: người không
          có quyền thì nó không render và không gọi API nào. Dự án chưa nối Git thì
          cũng không render, trừ khi việc đã có liên kết — khi ấy chỉ đọc (APM-GIT-q).
        */}
          <GitDevelopmentBlock
            projectId={task.projectId}
            target={{ kind: 'task', id: task.id }}
            subject={branchSubjectOfTask(task)}
            description={task.description}
            className="border-b border-border px-4 py-3"
            collapsible
          />
          {/* Commit người làm gắn khi tick / hoàn thành — APM-PCOM-d. Rỗng ⇒ không chiếm chỗ. */}
          <TaskProgressCommits
            projectId={task.projectId}
            taskId={task.id}
            className="border-b border-border px-4 py-3"
          />

          {/*
          Checklist đứng **trên** ba tab, không phải một tab thứ tư.

          Nó là điều kiện để đóng việc, nên nó phải đọc được ngay khi mở task —
          còn bình luận, hoạt động và tệp là những thứ người ta chủ động đi tìm.
          Dự án chưa đặt mục nào thì component tự trả `null`, không chiếm chỗ.
        */}
          <div className="px-4 pt-3 empty:hidden">
            <TaskDodChecklist taskId={task.id} canEdit={canEdit} />
          </div>

          <nav
            className="flex gap-1 border-b border-border px-4 py-2"
            aria-label={t('collaboration.detailTabs')}
          >
            {(['comments', 'activity', 'attachments'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                aria-current={tab === value}
                className={cn(
                  'rounded-sm px-3 py-1 text-sm transition-colors',
                  tab === value
                    ? 'bg-muted font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t(`collaboration.tab.${value}`)}
              </button>
            ))}
          </nav>

          <div className="p-4">
            {tab === 'comments' && (
              <CommentThread
                taskId={task.id}
                projectId={task.projectId}
                currentUserId={currentUserId}
                canDelete={commentsDeletable}
              />
            )}
            {tab === 'activity' && <ActivityFeed taskId={task.id} />}
            {/*
            Lỗi mà việc này đang sửa — docs/plan-testing-tab.md §9.2.

            Đứng ở tab Bình luận chứ không thành tab thứ tư: chưa tới 5% task là
            việc sửa lỗi, và một tab luôn trống trên 95% số task là nhiễu. Khối
            này tự biến mất khi không có lỗi nào gắn vào.
          */}
            {tab === 'comments' && <TaskBugLinks projectId={task.projectId} taskId={task.id} />}
            {tab === 'attachments' && (
              <AttachmentList
                taskId={task.id}
                projectId={task.projectId}
                canEdit={canEdit}
                canUpload={attachmentsUploadable}
              />
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
